/**
 * Notification Service
 *
 * API service module for user notifications.
 */

import { apiClient, extractData } from './api';
import type { Notification } from '@mellify/shared';

// ─── Types ──────────────────────────────────────────────────────────────────

export interface ListNotificationsParams {
  page?: number;
  limit?: number;
  read?: boolean;
  type?: string;
  types?: string;
  startDate?: string;
  endDate?: string;
}

export interface NotificationListResult {
  notifications: Notification[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  hasMore: boolean;
}

export interface NotificationCountResponse {
  unreadCount: number;
}

export interface ClearAllNotificationsResponse {
  deletedCount: number;
}

export interface NotificationVolumeByType {
  type: string;
  count: number;
  readCount: number;
  unreadCount: number;
  archivedCount: number;
}

export interface DeliveryRateByChannel {
  channel: string;
  totalSent: number;
  totalDelivered: number;
  totalFailed: number;
  deliveryRate: number;
}

export interface NotificationAnalyticsResult {
  period: {
    startDate: string;
    endDate: string;
  };
  summary: {
    totalNotifications: number;
    totalRead: number;
    totalUnread: number;
    totalArchived: number;
    readRate: number;
  };
  volumeByType: NotificationVolumeByType[];
  deliveryRates: DeliveryRateByChannel[];
  emailOpenRates: {
    totalEmailsSent: number;
    totalOpened: number;
    openRate: number;
  };
  dailyVolume: { date: string; count: number }[];
}

// ─── Notification Service ───────────────────────────────────────────────────

export const notificationService = {
  /**
   * List notifications for the current user in a workspace.
   */
  list: (
    workspaceId: string,
    params?: ListNotificationsParams
  ): Promise<NotificationListResult> =>
    extractData(
      apiClient.get<NotificationListResult>(
        `/workspaces/${workspaceId}/notifications`,
        { params }
      )
    ),

  /**
   * Get the unread notification count.
   */
  getUnreadCount: (workspaceId: string): Promise<NotificationCountResponse> =>
    extractData(
      apiClient.get<NotificationCountResponse>(
        `/workspaces/${workspaceId}/notifications/unread-count`
      )
    ),

  /**
   * Mark a notification as read.
   */
  markAsRead: (workspaceId: string, notificationId: string): Promise<void> =>
    extractData(
      apiClient.put(
        `/workspaces/${workspaceId}/notifications/${notificationId}/read`
      )
    ),

  /**
   * Mark all notifications as read.
   */
  markAllAsRead: (workspaceId: string): Promise<{ markedCount: number }> =>
    extractData(
      apiClient.put(`/workspaces/${workspaceId}/notifications/mark-all-read`)
    ),

  /**
   * Delete a notification.
   */
  delete: (workspaceId: string, notificationId: string): Promise<void> =>
    extractData(
      apiClient.delete(
        `/workspaces/${workspaceId}/notifications/${notificationId}`
      )
    ),

  /**
   * Clear all notifications for the current user in a workspace.
   */
  clearAll: (workspaceId: string): Promise<ClearAllNotificationsResponse> =>
    extractData(
      apiClient.delete(`/workspaces/${workspaceId}/notifications`)
    ),

  /**
   * Get notification analytics for a workspace.
   */
  getAnalytics: (
    workspaceId: string,
    params?: { startDate?: string; endDate?: string; days?: number }
  ): Promise<NotificationAnalyticsResult> =>
    extractData(
      apiClient.get<NotificationAnalyticsResult>(
        `/workspaces/${workspaceId}/notifications/analytics`,
        { params }
      )
    ),
};